import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  Mail,
  ExternalLink,
  Briefcase,
  Building2,
  BookOpen,
  Calendar
} from 'lucide-react';
import { LinkedInSolid } from '../components/icons/LinkedInSolid';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TEAM_PATH = '/team';

function getYoutubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m) return m[1];
  }
  return null;
}

function getPostThumbnailUrl(post) {
  if (post.featured_image_url) {
    return post.featured_image_url.startsWith('/') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url;
  }
  const videoId = getYoutubeVideoId(post.youtube_url);
  if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  return null;
}

const CATEGORY_MAPPING = {
  general_partners: 'General Partners',
  venture_partners: 'Venture Partners',
  investment_sourcing: 'Team',
  investment_management: 'Team',
  research_and_strategy: 'Team',
  investor_relations: 'Team',
  finance: 'Team',
  esg: 'Team',
  investment_growth: 'Team',
  human_resources: 'Team',
  efficiency_and_effectiveness: 'Team',
  brand_management: 'Team',
  founders_office: 'Team',
  advisors: 'Mentors and Advisors'
};

// Full display label for badge (Team - <name>)
const TEAM_TYPE_LABELS = {
  general_partners: 'General Partners',
  venture_partners: 'Venture Partners',
  investment_sourcing: 'Team - Investment Sourcing',
  investment_management: 'Team - Investment Management',
  research_and_strategy: 'Team - Research & Strategy',
  investor_relations: 'Team - Investor Relations',
  finance: 'Team - Finance',
  esg: 'Team - ESG',
  investment_growth: 'Team - Investment Growth',
  human_resources: 'Team - People & Talent',
  efficiency_and_effectiveness: 'Team - Efficiency & Effectiveness',
  brand_management: 'Team - Brand Management',
  founders_office: "Team - MD's Office",
  advisors: 'Mentors and Advisors'
};

const parseThreeWords = (value) => {
  if (!value || typeof value !== 'string') return [];
  // Accept comma-separated or space-separated; normalize to at most 3 items.
  const parts = value.includes(',')
    ? value.split(',').map(s => s.trim()).filter(Boolean)
    : value.split(/\s+/).map(s => s.trim()).filter(Boolean);
  // De-dupe while preserving order
  const unique = [];
  for (const p of parts) {
    if (!unique.includes(p)) unique.push(p);
  }
  return unique.slice(0, 3);
};

const PortfolioCard = ({ company }) => {
  return (
    <Link
      to={`/portfolio`}
      className="group block bg-white rounded-xl p-4 border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-lg transition-all duration-300"
      data-testid={`portfolio-link-${company.company_id}`}
      aria-label={`View ${company.name} on portfolio page`}
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0">
          <img
            src={company.logo_url?.startsWith('/') ? `${BACKEND_URL}${company.logo_url}` : company.logo_url || `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`}
            alt={company.logo_alt?.trim() || company.name}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=C8473A&color=fff`;
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors truncate">
            {company.name}
          </h4>
          {company.sectors?.length > 0 && (
            <p className="text-xs text-gray-500 truncate">
              {company.sectors.slice(0, 2).join(', ')}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

const TeamMemberPage = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [member, setMember] = useState(null);
  const [authoredPosts, setAuthoredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageTitle(member?.name || (loading ? 'Team' : 'Team member'));
  const [error, setError] = useState(null);

  const handleBackToTeam = () => {
    navigate(TEAM_PATH);
  };

  const fetchMember = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team/${memberId}`);
      const data = response.data;
      setMember(data);
      // Canonicalize legacy member_id or outdated slug URLs without dropping navigation state.
      if (data.slug && memberId !== data.slug) {
        navigate(`/team/${data.slug}`, { replace: true, state: location.state });
      }
    } catch (err) {
      console.error('Failed to fetch team member:', err);
      setError('Team member not found');
    } finally {
      setLoading(false);
    }
  }, [memberId, navigate, location.state]);

  const fetchAuthoredPosts = useCallback(async (authorMemberId) => {
    try {
      const response = await axios.get(`${API}/admin/blog/public/by-author/${authorMemberId}`);
      setAuthoredPosts(response.data);
    } catch (err) {
      console.error('Failed to fetch authored posts:', err);
    }
  }, []);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  useEffect(() => {
    if (member?.member_id) {
      fetchAuthoredPosts(member.member_id);
    }
  }, [member?.member_id, fetchAuthoredPosts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-32 mb-8"></div>
              <div className="grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-1">
                  <div className="aspect-[3/4] bg-gray-200 rounded-2xl"></div>
                </div>
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-10 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-100 rounded w-1/2"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-full"></div>
                    <div className="h-4 bg-gray-100 rounded w-full"></div>
                    <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
            <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h1 className="text-2xl font-bold text-[#211F1F] mb-2">Team Member Not Found</h1>
            <p className="text-gray-500 mb-6">The team member you're looking for doesn't exist or has been removed.</p>
            <Button onClick={handleBackToTeam} aria-label="Back to Team">
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Team
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const teamTypeLabel = TEAM_TYPE_LABELS[member.team_type] || (member.team_type ? `Team - ${member.team_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}` : null);
  const threeWords = parseThreeWords(member.three_words);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              type="button"
              onClick={handleBackToTeam}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-8 transition-colors"
              data-testid="back-to-team"
              aria-label="Back to Team"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Team
            </button>
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Photo & Socials */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-1"
            >
              {/* Photo */}
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 mb-6 shadow-lg">
                <img
                  src={member.photo_url?.startsWith('/') ? `${BACKEND_URL}${member.photo_url}` : member.photo_url || `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=400`}
                  alt={member.photo_alt || member.name}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=400`;
                  }}
                />
              </div>

              {/* Social Links */}
              <div className="flex flex-wrap gap-3">
                {member.linkedin_url && (
                  <a
                    href={member.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${member.name} on LinkedIn (opens in new tab)`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-[#0A66C2] hover:bg-[#0A66C2]/5 transition-all text-sm font-medium"
                    data-testid="linkedin-link"
                  >
                    <LinkedInSolid className="w-4 h-4 text-[#0A66C2]" aria-hidden="true" />
                    LinkedIn
                  </a>
                )}
                {member.twitter_url && (
                  <a
                    href={member.twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${member.name} on X (opens in new tab)`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-black hover:bg-black/5 transition-all text-sm font-medium"
                    data-testid="x-link"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X
                  </a>
                )}
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    aria-label={`Email ${member.name}`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-[#C8473A] hover:bg-[#C8473A]/5 transition-all text-sm font-medium"
                    data-testid="email-link"
                  >
                    <Mail className="w-4 h-4 text-[#C8473A]" aria-hidden="true" />
                    Email
                  </a>
                )}
              </div>
            </motion.div>

            {/* Right Column - Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              {/* Team Type Badge */}
              {teamTypeLabel && (
                <Badge variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint mb-4">
                  {teamTypeLabel}
                </Badge>
              )}

              {/* Name & Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-2">
                {member.name}
              </h1>
              <p className="text-xl text-gray-600 mb-6">{member.designation}</p>

              {/* Three Words (above capabilities) */}
              {threeWords.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                    In three words
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {threeWords.map((word) => (
                      <Badge
                        key={word}
                        variant="secondary"
                        className="bg-[#C8473A]/10 text-yn-brand-on-tint"
                      >
                        {word}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Capabilities */}
              {member.capabilities?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Capabilities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {member.capabilities.map((cap, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="border-gray-300 text-gray-700 bg-white"
                      >
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Bio (content only; no "About" header) */}
              {member.bio && (
                <div className="mb-8">
                  {String(member.bio)
                    .split(/\n{2,}/g)
                    .map((para, idx) => (
                      <p key={idx} className="text-gray-700 leading-relaxed mb-4 last:mb-0">
                        {para}
                      </p>
                    ))}
                </div>
              )}

              {/* Portfolio Companies */}
              {member.portfolio_companies?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Portfolio Companies ({member.portfolio_companies.length})
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {member.portfolio_companies.map((company) => (
                      <PortfolioCard key={company.company_id} company={company} />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State for Portfolio */}
              {(!member.portfolio_companies || member.portfolio_companies.length === 0) && member.member_type === 'partner' && (
                <div className="bg-gray-50 rounded-xl p-6 text-center mb-8">
                  <Building2 className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No portfolio companies associated yet</p>
                </div>
              )}

              {/* Authored Posts */}
              {authoredPosts.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Authored Posts ({authoredPosts.length})
                  </h3>
                  <div className="space-y-3">
                    {authoredPosts.map((post) => (
                      <Link
                        key={post.post_id}
                        to={`/insights/${post.slug}`}
                        className="group flex gap-4 p-3 bg-white rounded-xl border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-lg transition-all duration-300"
                        data-testid={`authored-post-${post.slug}`}
                      >
                        {(post.featured_image_url || post.youtube_url) && (() => {
                          const thumb = getPostThumbnailUrl(post);
                          return thumb ? (
                            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={thumb}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          ) : null;
                        })()}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#211F1F] group-hover:text-[#C8473A] transition-colors line-clamp-2 mb-1">
                            {post.title}
                          </h4>
                          {post.excerpt && (
                            <p className="text-sm text-gray-500 line-clamp-1 mb-2">{post.excerpt}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-light">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {post.category && (
                              <span className="px-2 py-0.5 bg-gray-100 rounded-full">{post.category}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TeamMemberPage;
